<template>
	<div class="container">
		<div v-if="loading">
			Loading...
		</div>

		<div v-else-if="error">
			There was an error while loading this product.
		</div>

		<div v-else>
			<div class="row">
				<div class="col-md-8">		
					<ux-form>			
						<!-- SIZING INTERFACE -->
						<product-sizing id="sizing" />

						<!-- MOUNTING & CONTROL OPTIONS -->
						<mounting-options />

						<!-- PRODUCT OPTIONS -->
						<option-selector :id="`option-selector-${type.system_name}`" v-for="type in optionTypes" :key="type.id" :option-type="type"/>
					</ux-form>
				</div>

				<!-- PRODUCT SUMMARY -->
				<div class="col-md-4">
					<product-summary />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import zenscroll from 'zenscroll';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import alerts from 'common/alerts';

import ProductSizing from './ProductSizing.vue';
import MountingOptions from './MountingOptions.vue';
import OptionSelector from './OptionSelector.vue';
import ProductSummary from './ProductSummary.vue';

import { controlOptions } from '../options';

export default {
	components: { ProductSizing, OptionSelector, ProductSummary, MountingOptions },

	provide()
	{
		return {
			validateProduct: this.validate,
		}
	},

	props: {
		productSlug: { type: String, required: true },
	},

	data() {
		return {
			loading: true,
			error: false,
		}
	},

	computed: {
		...mapGetters('customizer', ['allSelectedOptions', 'productWidthOptions', 'productHeightOptions']),

		...mapGetters('shell', { 
			optionTypes: 'productOptionTypeList',
		}),

		...mapState({
			cartItem: state => state.customizer.cartItem,
			product: state => state.customizer.product,
			optionsByType: state => state.customizer.optionsByType,
			headrailNum: state => state.customizer.headrailNum,
			headrailSizes: state => state.customizer.headrailSizes,
			optionSelections: state => state.customizer.optionSelections,
		}),

		optionTypeList()
		{
			return this.optionTypes.filter(o => this.optionsByType[o.id]);
		},

		// this is probably an ugly way to watch for changes to mutliple state fields but its also easier to modify?
		watchableItems()
		{
			return {
				cartItem: this.cartItem,
				headrails: this.headrailNum,
				headreailSizes: this.headrailSizes,
				allSelectedOptions: this.allSelectedOptions,
			}
		}
	},

	watch: {
		cartItem: {
			deep: true,
			handler() {
				this.filterSelectableOptions();
			}
		},

		watchableItems: {
			deep: true,
			handler() {
				this.fetchSummary();
			}
		}
	},

	methods: {
		...mapMutations('customizer', ['setCartItem', 'setHeadrailControls']),
		...mapActions('customizer', ['fetchProductInfo', 'filterSelectableOptions', 'fetchSummary']),

		async load()
		{
			this.loading = true;
			this.error = false;

			try
			{
				var widthWhole, widthFract, heightWhole, heightFract;
				
				// pull product data from server
				await this.fetchProductInfo({ slug: this.productSlug });

				// look for width/height in query string
				widthWhole = this.$route.query.width_whole || this.product.width_options[0];
				widthFract = this.$route.query.width_fract || '0';
				heightWhole = this.$route.query.height_whole || this.product.height_options[0];
				heightFract = this.$route.query.height_fract || '0';
				
				// initialize cart item
				this.setCartItem({
					...this.cartItem,
					width: { whole: widthWhole, fractional: widthFract },
					height: { whole: heightWhole, fractional: heightFract },
				});

				// initialize control position
				this.setHeadrailControls(controlOptions[this.product.control_style][0].value);

				// initialize summary
				await this.fetchSummary();
			}
			catch(err)
			{
				this.error = true;
				console.error(err);
			}
			finally
			{
				this.loading = false;
			}
		},

		// used to make sure the selected width/height doesn't exceed the max size of this product
		validateSize(size, options)
		{
			// get position of whole in options
			var pos = options.indexOf(parseInt(size.whole, 10));

			// if this is the last option then fractional must only be zero
			if (pos == options.length - 1 && size.fractional != '0')
				return false;

			// otherwise we should be fine!
			return true;
		},

		async validate()
		{
			// make sure the user doesn't pick a size that exceeds the limits of the product
			if (!this.validateSize(this.cartItem.width, this.productWidthOptions))
			{
				var msg = `${this.cartItem.width.whole} ${this.cartItem.width.fractional}" exceeds the maximum width of this product`;
				await alerts.show(msg, 'Invalid Size', 'warning');
				zenscroll.to(document.getElementById('sizing'));
				return;
			}

			if (!this.validateSize(this.cartItem.height, this.productHeightOptions))
			{
				var msg = `${this.cartItem.height.whole} ${this.cartItem.height.fractional}" exceeds the maximum height of this product`;
				await alerts.show(msg, 'Invalid Size', 'warning');
				zenscroll.to(document.getElementById('sizing'));
				return;
			}


			// make sure the user enters the room they want
			if (this.cartItem.room.trim() == '')
			{
				await alerts.show('Please enter the room you\'d like to have the blinds installed in', 'Error', 'warning');
				zenscroll.to(document.getElementById('sizing'));
				return false;
			}

			// check for required options
			for(var type of this.optionTypes)
			{
				// skip if the option type is not required
				if (!type.is_required)
					continue;

				if (!this.optionSelections[type.id] || this.optionSelections[type.id].length == 0)
				{
					await alerts.show(`Please select an option under ${type.type_name}`, 'Missing Required Option', 'warning');
					zenscroll.to(document.getElementById(`option-selector-${type.system_name}`));
					return false;
				}
			}

			return true;
		}
	},

	mounted()
	{
		this.load();
	}
}
</script>